import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import {
  MoldFormDialogComponent,
  MoldFormDialogTypes,
} from '../../../../../common/mold/dialog/form/form.component';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MoldRevisionItem } from '../../../../../common/mold/models/item';
import { MatButtonModule } from '@angular/material/button';
import { MoldCreateFormButtonsDirective } from '../../../../../common/mold/dialog/form/form.directive';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MoldAssemblyItem } from '../../../../../api/elite/types/mold';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';
import { HosesAssemblyItemApiService } from './item.service';

export type HosesMoldFormDialogTypes = {
  data: MoldFormDialogTypes['data'] & {
    department: 'Hoses';
    selectItemCavities: true;
  };
  return: MoldFormDialogTypes['return'] & {
    itemCavities?: {
      assemblyItemId: number;
      cavities: number;
    }[];
  };
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    MoldFormDialogComponent,
    MatButtonModule,
    MatDialogModule,
    MoldCreateFormButtonsDirective,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    ReactiveFormsModule,
  ],
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.sass'],
  providers: [HosesAssemblyItemApiService],
})
export class HosesMoldFormDialogComponent implements OnInit, AfterViewInit {
  private readonly data: HosesMoldFormDialogTypes['data'] =
    inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly service = inject(HosesAssemblyItemApiService);
  private fb = inject(FormBuilder);

  assemblyItemsForm: FormGroup = this.fb.group({
    moldItems: this.fb.array([]),
  });

  get moldRevision(): MoldRevisionItem {
    return this.data.moldRevision;
  }

  get selectItemCavities(): boolean {
    return this.data.selectItemCavities;
  }

  get fieldsMoldItems(): FormArray {
    return this.assemblyItemsForm.get('moldItems') as FormArray;
  }

  get moldAssemblyItems(): MoldAssemblyItem[] {
    return this.moldRevision
      ? this.data.moldRevision.mold.moldAssemblyItems
      : [];
  }

  get selectedItemIds(): number[] {
    return this.fieldsMoldItems.controls
      .map((c) => c.get('item')?.value?.id)
      .filter((id): id is number => !!id);
  }

  afterViewLoad = false;
  extraSteps: {
    template: TemplateRef<any>;
    label: string;
  }[] = [];
  @ViewChild('templateAssemblyItem')
  moldItemTemplate: TemplateRef<any> | undefined = undefined;

  @ViewChild('formComponent') formComponent: MoldFormDialogComponent;

  constructor() {}

  ngOnInit(): void {
    if (this.data.selectItemCavities) {
      // this.showStepCavities = true;
      this.moldAssemblyItems.forEach((item) => {
        this.addAssemblyItem(
          { id: item.assemblyItemId, title: item.assemblyItem.title },
          item.cavities
        );
      });
    }
  }

  ngAfterViewInit(): void {
    this.extraSteps = this.selectItemCavities
      ? [
          {
            template: this.moldItemTemplate,
            label: 'Cortes especificos',
          },
        ]
      : [];
    this.afterViewLoad = true;
  }

  closeDialog(): void {
    this.dialogRef.close({
      ...this.formComponent.resultData(),
      itemCavities: this.fieldsMoldItems.value.map((itemMold) => {
        return {
          assemblyItemId: itemMold.item.id,
          cavities: itemMold.cavities,
        };
      }),
    });
  }

  displayFn = (item: { id: number; title: string }): string => {
    return item?.title || '';
  };

  private createCavityFormAssign(
    item?: { id: number; title: string },
    cavities?: number
  ): FormGroup {
    return this.fb.group({
      item: [item, Validators.required],
      cavities: [cavities, [Validators.required]],
      options$: [],
    });
  }

  addAssemblyItem(
    item?: { id: number; title: string },
    cavities?: number
  ): void {
    const newAssign = this.createCavityFormAssign(item, cavities);
    const options$ = newAssign.get('item').valueChanges.pipe(
      debounceTime(300),
      filter((val) => typeof val === 'string'),
      distinctUntilChanged(),
      switchMap((term) => {
        return this.service
          .searchItems(term || '')
          .pipe(
            map((results) =>
              results.filter((op) => !this.selectedItemIds.includes(op.id))
            )
          );
      })
    );
    newAssign.patchValue({ options$ }, { emitEvent: false });
    this.fieldsMoldItems.push(newAssign);
  }

  removeAssemblyItem(index: number): void {
    if (this.fieldsMoldItems.length < 1) {
      return;
    }
    this.fieldsMoldItems.removeAt(index);
  }
}
